import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ChevronRight, EyeOff, Flag, Loader2, MessageCircle, RotateCcw, Send, Trash2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { cn } from '@/lib/utils';
import { trackProductEvent } from '@/lib/product-analytics';
import {
  ADULTING_COMMUNITY_CHANNEL_GROUPS,
  ADULTING_COMMUNITY_CHANNELS,
  getAdultingCommunityChannel,
} from '../../../ui-foundation/features/community';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppChipButton,
  AppEmptyState,
  AppErrorState,
  AppFieldGroup,
  AppInlineAlert,
  AppLoadingState,
  AppPanelButton,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import {
  AppBadge as Badge,
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogDescription as DialogDescription,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
  AppTabs as Tabs,
  AppTabsContent as TabsContent,
  AppTabsList as TabsList,
  AppTabsTrigger as TabsTrigger,
} from '../../../ui-foundation/adapters/web';

const ALL_CHANNEL_OPTION = {
  key: 'all',
  label: 'All channels',
  emoji: '✨',
  description: 'Browse the channel directory instead of one long shared feed.',
};

const REPORT_REASONS = [
  'Feels unsafe or harmful',
  'Spam or selling',
  'Judgmental or shaming',
  'Looks like misinformation',
] as const;

type CommunityTopic = (typeof ADULTING_COMMUNITY_CHANNELS)[number]['key'];
type CommunityFeedFilter = CommunityTopic | 'all';
type CommunityView = 'channels' | 'messages';

type CommunityPost = Tables<'community_posts'>;
type CommunityReply = Tables<'community_replies'>;
type CommunityDmThreadRow = {
  id: string;
  created_at: string;
  last_message_at: string;
};
type CommunityDmThreadMemberRow = {
  thread_id: string;
  user_id: string;
  member_name: string;
};
type CommunityDmMessageRow = {
  id: string;
  thread_id: string;
  user_id: string;
  author_name: string;
  body: string;
  created_at: string;
};
type CommunityDmMemberOption = {
  userId: string;
  name: string;
};

function getTopicMeta(topic: CommunityFeedFilter) {
  if (topic === 'all') return ALL_CHANNEL_OPTION;
  return getAdultingCommunityChannel(topic);
}

function getDisplayName(firstName?: string, email?: string | null) {
  const cleanedName = firstName?.trim();
  if (cleanedName) return cleanedName;

  const localPart = email?.split('@')[0]?.replace(/[._+-]+/g, ' ').trim();
  if (!localPart) return 'Adulting member';

  return localPart
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function timeAgo(value: string) {
  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

export default function CommunityTab() {
  const { user, data } = useApp();
  const { isAdminOrMod } = useUserRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [communityWarnings, setCommunityWarnings] = useState<string[]>([]);
  const [posting, setPosting] = useState(false);
  const [postBody, setPostBody] = useState('');
  const queryView = searchParams.get('view');
  const initialCommunityView: CommunityView = queryView === 'messages' ? queryView : 'channels';
  const [communityView, setCommunityView] = useState<CommunityView>(initialCommunityView);
  const [selectedCircle, setSelectedCircle] = useState<CommunityFeedFilter>('all');
  const [selectedPostTopic, setSelectedPostTopic] = useState<CommunityTopic>('general');
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [replies, setReplies] = useState<CommunityReply[]>([]);
  const [memberAccessStatus, setMemberAccessStatus] = useState(data.profile.communityAccessStatus);
  const [openReplyFor, setOpenReplyFor] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [savingReplyFor, setSavingReplyFor] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [dmLoading, setDmLoading] = useState(true);
  const [dmLoadError, setDmLoadError] = useState<string | null>(null);
  const [dmThreads, setDmThreads] = useState<CommunityDmThreadRow[]>([]);
  const [dmThreadMembers, setDmThreadMembers] = useState<CommunityDmThreadMemberRow[]>([]);
  const [dmMessages, setDmMessages] = useState<CommunityDmMessageRow[]>([]);
  const [dmMemberOptions, setDmMemberOptions] = useState<CommunityDmMemberOption[]>([]);
  const [selectedDmRecipientId, setSelectedDmRecipientId] = useState('');
  const [selectedDmThreadId, setSelectedDmThreadId] = useState<string | null>(null);
  const [newDmBody, setNewDmBody] = useState('');
  const [dmReplyBody, setDmReplyBody] = useState('');
  const [dmSaving, setDmSaving] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: 'post' | 'reply'; id: string; preview: string } | null>(null);
  const [reportReason, setReportReason] = useState<(typeof REPORT_REASONS)[number]>(REPORT_REASONS[0]);
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting] = useState(false);

  const authorName = useMemo(
    () => getDisplayName(data.profile.firstName, user?.email),
    [data.profile.firstName, user?.email]
  );

  useEffect(() => {
    if (initialCommunityView !== communityView) {
      setCommunityView(initialCommunityView);
    }
  }, [communityView, initialCommunityView]);

  const updateCommunityView = useCallback(
    (nextView: CommunityView) => {
      setCommunityView(nextView);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('view', nextView);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const loadCommunity = useCallback(
    async (withLoader = false) => {
      if (!user) {
        setLoadError(null);
        setCommunityWarnings([]);
        setLoading(false);
        return;
      }

      if (withLoader) setLoading(true);
      setLoadError(null);

      try {
        const warnings: string[] = [];
        const [profileResult, postsResult] = await Promise.all([
          supabase
            .from('profiles')
            .select('community_access_status')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase
            .from('community_posts')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(40),
        ]);

        if (postsResult.error) throw postsResult.error;

        if (profileResult.error) {
          console.error(profileResult.error);
          warnings.push('Community access status is temporarily unavailable, so Adulting is using your last known access state.');
        }

        const nextPosts = (postsResult.data || []) as CommunityPost[];
        const postIds = nextPosts.map((post) => post.id);
        const repliesResult = postIds.length
          ? await supabase
              .from('community_replies')
              .select('*')
              .in('post_id', postIds)
              .order('created_at', { ascending: true })
          : { data: [], error: null };

        if (repliesResult.error) {
          console.error(repliesResult.error);
          warnings.push('Replies could not be loaded, so community is showing posts only for now.');
        }

        setMemberAccessStatus((profileResult.data?.community_access_status || data.profile.communityAccessStatus) as typeof memberAccessStatus);
        setPosts(nextPosts);
        setReplies(((repliesResult.error ? [] : repliesResult.data) as CommunityReply[]) || []);
        setCommunityWarnings(warnings);
      } catch (error) {
        console.error(error);
        setCommunityWarnings([]);
        setLoadError('Could not load the community right now.');
        toast.error('Could not load the community right now.');
      } finally {
        if (withLoader) setLoading(false);
      }
    },
    [data.profile.communityAccessStatus, user]
  );

  useEffect(() => {
    void loadCommunity(true);
  }, [loadCommunity]);

  useEffect(() => {
    setMemberAccessStatus(data.profile.communityAccessStatus);
  }, [data.profile.communityAccessStatus]);

  const repliesByPost = useMemo(() => {
    return replies.reduce<Record<string, CommunityReply[]>>((acc, reply) => {
      if (!acc[reply.post_id]) acc[reply.post_id] = [];
      acc[reply.post_id].push(reply);
      return acc;
    }, {});
  }, [replies]);

  const fallbackDmMemberOptions = useMemo<CommunityDmMemberOption[]>(() => {
    const seen = new Map<string, string>();
    posts.forEach((post) => {
      if (post.user_id !== user?.id && post.status === 'active' && post.user_id) {
        seen.set(post.user_id, post.author_name);
      }
    });
    replies.forEach((reply) => {
      if (reply.user_id !== user?.id && reply.status === 'active' && reply.user_id) {
        seen.set(reply.user_id, reply.author_name);
      }
    });

    return Array.from(seen.entries())
      .map(([userId, name]) => ({ userId, name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [posts, replies, user?.id]);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesCircle = selectedCircle === 'all' || post.topic === selectedCircle;
      const canSee = post.status === 'active' || post.user_id === user?.id || isAdminOrMod;
      return matchesCircle && canSee;
    });
  }, [posts, selectedCircle, user?.id, isAdminOrMod]);

  const visiblePostsByChannel = useMemo(
    () =>
      ADULTING_COMMUNITY_CHANNELS.reduce<Record<CommunityTopic, CommunityPost[]>>((acc, channel) => {
        acc[channel.key] = posts.filter((post) => {
          const canSee = post.status === 'active' || post.user_id === user?.id || isAdminOrMod;
          return post.topic === channel.key && canSee;
        });
        return acc;
      }, {} as Record<CommunityTopic, CommunityPost[]>),
    [posts, user?.id, isAdminOrMod]
  );

  const loadDirectMessages = useCallback(async () => {
    if (!user) {
      setDmThreads([]);
      setDmThreadMembers([]);
      setDmMessages([]);
      setDmMemberOptions([]);
      setSelectedDmThreadId(null);
      setDmLoadError(null);
      setDmLoading(false);
      return;
    }

    setDmLoading(true);
    setDmLoadError(null);

    try {
      const [profilesResult, myMembershipsResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('user_id, first_name')
          .eq('community_access_status', 'active')
          .neq('user_id', user.id),
        supabase
          .from('community_dm_thread_members' as any)
          .select('thread_id')
          .eq('user_id', user.id),
      ]);

      if (myMembershipsResult.error) throw myMembershipsResult.error;

      const memberOptions = profilesResult.error
        ? fallbackDmMemberOptions
        : (((profilesResult.data || []) as Array<{ user_id: string; first_name: string | null }>)
            .map((row) => ({
              userId: row.user_id,
              name: getDisplayName(row.first_name || undefined),
            }))
            .sort((left, right) => left.name.localeCompare(right.name)));

      const threadIds = Array.from(
        new Set((((myMembershipsResult.data || []) as Array<{ thread_id: string }>)).map((row) => row.thread_id))
      );

      if (!threadIds.length) {
        setDmThreads([]);
        setDmThreadMembers([]);
        setDmMessages([]);
        setDmMemberOptions(memberOptions);
        setSelectedDmThreadId(null);
        setDmLoading(false);
        return;
      }

      const [threadsResult, membersResult, messagesResult] = await Promise.all([
        supabase
          .from('community_dm_threads' as any)
          .select('id, created_at, last_message_at')
          .in('id', threadIds)
          .order('last_message_at', { ascending: false }),
        supabase
          .from('community_dm_thread_members' as any)
          .select('thread_id, user_id, member_name')
          .in('thread_id', threadIds),
        supabase
          .from('community_dm_messages' as any)
          .select('id, thread_id, user_id, author_name, body, created_at')
          .in('thread_id', threadIds)
          .order('created_at', { ascending: true }),
      ]);

      if (threadsResult.error) throw threadsResult.error;
      if (membersResult.error) throw membersResult.error;
      if (messagesResult.error) throw messagesResult.error;

      const nextThreads = (threadsResult.data || []) as CommunityDmThreadRow[];
      setDmThreads(nextThreads);
      setDmThreadMembers((membersResult.data || []) as CommunityDmThreadMemberRow[]);
      setDmMessages((messagesResult.data || []) as CommunityDmMessageRow[]);
      setDmMemberOptions(memberOptions);
      setSelectedDmThreadId((current) => (current && nextThreads.some((thread) => thread.id === current) ? current : null));
    } catch (error) {
      console.error(error);
      setDmLoadError('Could not load direct messages right now.');
    } finally {
      setDmLoading(false);
    }
  }, [fallbackDmMemberOptions, user]);

  useEffect(() => {
    void loadDirectMessages();
  }, [loadDirectMessages]);

  const communityAccessStatus = memberAccessStatus;
  const communityReadOnly = communityAccessStatus !== 'active';
  const selectedCircleMeta = getTopicMeta(selectedCircle);
  const dmMessagesByThread = useMemo(() => {
    return dmMessages.reduce<Record<string, CommunityDmMessageRow[]>>((acc, message) => {
      if (!acc[message.thread_id]) acc[message.thread_id] = [];
      acc[message.thread_id].push(message);
      return acc;
    }, {});
  }, [dmMessages]);
  const dmThreadSummaries = useMemo(() => {
    return dmThreads.map((thread) => {
      const members = dmThreadMembers.filter((member) => member.thread_id === thread.id);
      const otherMembers = members.filter((member) => member.user_id !== user?.id);
      const latestMessage = (dmMessagesByThread[thread.id] || []).slice(-1)[0] || null;
      return {
        id: thread.id,
        title: otherMembers.map((member) => member.member_name).join(', ') || 'Direct message',
        memberIds: members.map((member) => member.user_id),
        latestMessage,
        lastMessageAt: latestMessage?.created_at || thread.last_message_at,
      };
    });
  }, [dmMessagesByThread, dmThreadMembers, dmThreads, user?.id]);
  const selectedDmThreadMessages = selectedDmThreadId ? (dmMessagesByThread[selectedDmThreadId] || []) : [];
  const selectedDmThreadSummary = selectedDmThreadId
    ? dmThreadSummaries.find((thread) => thread.id === selectedDmThreadId) || null
    : null;
  const communityViewOptions = [
    { value: 'channels' as const, label: 'Channels', detail: ADULTING_COMMUNITY_CHANNELS.length },
    { value: 'messages' as const, label: 'Messages', detail: dmThreadSummaries.length },
  ];

  const openChannel = (topic: CommunityTopic) => {
    setSelectedCircle(topic);
    setSelectedPostTopic(topic);
    updateCommunityView('channels');
  };

  const startDirectMessage = async () => {
    if (!user) return;

    if (!selectedDmRecipientId) {
      toast.error('Choose a member first.');
      return;
    }

    const body = newDmBody.trim();
    if (!body) {
      toast.error('Write the first message before starting the thread.');
      return;
    }

    setDmSaving(true);

    try {
      let threadId = dmThreadSummaries.find((thread) => {
        const uniqueMembers = Array.from(new Set(thread.memberIds));
        return uniqueMembers.length === 2 && uniqueMembers.includes(user.id) && uniqueMembers.includes(selectedDmRecipientId);
      })?.id;

      if (!threadId) {
        const recipientName =
          dmMemberOptions.find((option) => option.userId === selectedDmRecipientId)?.name
          || fallbackDmMemberOptions.find((option) => option.userId === selectedDmRecipientId)?.name
          || 'Adulting member';

        const { data: threadRow, error: threadError } = await supabase
          .from('community_dm_threads' as any)
          .insert({ created_by: user.id })
          .select('id')
          .single();

        if (threadError) throw threadError;

        threadId = threadRow.id;

        const { error: membersError } = await supabase.from('community_dm_thread_members' as any).insert([
          {
            thread_id: threadId,
            user_id: user.id,
            member_name: authorName,
          },
          {
            thread_id: threadId,
            user_id: selectedDmRecipientId,
            member_name: recipientName,
          },
        ]);

        if (membersError) throw membersError;
      }

      const { error: messageError } = await supabase.from('community_dm_messages' as any).insert({
        thread_id: threadId,
        user_id: user.id,
        author_name: authorName,
        body,
      });

      if (messageError) throw messageError;

      setNewDmBody('');
      setSelectedDmThreadId(threadId);
      updateCommunityView('messages');
      toast.success('Direct message sent.');
      await loadDirectMessages();
    } catch (error) {
      console.error(error);
      toast.error('Could not start that direct message right now.');
    } finally {
      setDmSaving(false);
    }
  };

  const sendDirectMessage = async () => {
    if (!user || !selectedDmThreadId) return;

    const body = dmReplyBody.trim();
    if (!body) {
      toast.error('Write a message first.');
      return;
    }

    setDmSaving(true);

    try {
      const { error } = await supabase.from('community_dm_messages' as any).insert({
        thread_id: selectedDmThreadId,
        user_id: user.id,
        author_name: authorName,
        body,
      });

      if (error) throw error;

      setDmReplyBody('');
      toast.success('Message sent.');
      await loadDirectMessages();
    } catch (error) {
      console.error(error);
      toast.error('Could not send that message right now.');
    } finally {
      setDmSaving(false);
    }
  };

  const createPost = async () => {
    if (!user) return;
    if (communityReadOnly) {
      toast.error('Community posting is currently paused on this account.');
      return;
    }

    const body = postBody.trim();
    if (!body) {
      toast.error('Write something before posting.');
      return;
    }

    setPosting(true);

    const { error } = await supabase.from('community_posts').insert({
      user_id: user.id,
      author_name: authorName,
      body,
      topic: selectedPostTopic,
    });

    if (error) {
      console.error(error);
      toast.error('Could not post right now.');
    } else {
      void trackProductEvent({
        userId: user.id,
        eventName: 'community_post_created',
        path: '/health',
        metadata: {
          topic: selectedPostTopic,
        },
      });
      setPostBody('');
      toast.success('Post shared.');
      await loadCommunity();
    }

    setPosting(false);
  };

  const saveReply = async (postId: string) => {
    if (!user) return;
    if (communityReadOnly) {
      toast.error('Community replies are currently paused on this account.');
      return;
    }

    const body = replyDrafts[postId]?.trim();
    if (!body) {
      toast.error('Write a reply before posting.');
      return;
    }

    setSavingReplyFor(postId);

    const { error } = await supabase.from('community_replies').insert({
      post_id: postId,
      user_id: user.id,
      author_name: authorName,
      body,
    });

    if (error) {
      console.error(error);
      toast.error('Could not add your reply.');
    } else {
      setReplyDrafts((current) => ({ ...current, [postId]: '' }));
      setOpenReplyFor(null);
      toast.success('Reply added.');
      await loadCommunity();
    }

    setSavingReplyFor(null);
  };

  const deletePost = async (postId: string) => {
    if (!window.confirm('Delete this post? This also removes its replies.')) return;

    setActioningId(postId);

    const { error } = await supabase.from('community_posts').delete().eq('id', postId);

    if (error) {
      console.error(error);
      toast.error('Could not delete that post.');
    } else {
      toast.success('Post deleted.');
      await loadCommunity();
    }

    setActioningId(null);
  };

  const deleteReply = async (replyId: string) => {
    if (!window.confirm('Delete this reply?')) return;

    setActioningId(replyId);

    const { error } = await supabase.from('community_replies').delete().eq('id', replyId);

    if (error) {
      console.error(error);
      toast.error('Could not delete that reply.');
    } else {
      toast.success('Reply deleted.');
      await loadCommunity();
    }

    setActioningId(null);
  };

  const updatePostStatus = async (post: CommunityPost, status: 'active' | 'hidden') => {
    setActioningId(post.id);

    const { error } = await supabase.from('community_posts').update({ status }).eq('id', post.id);

    if (error) {
      console.error(error);
      toast.error(`Could not ${status === 'hidden' ? 'hide' : 'restore'} that post.`);
    } else {
      toast.success(status === 'hidden' ? 'Post hidden from community.' : 'Post restored.');
      await loadCommunity();
    }

    setActioningId(null);
  };

  const updateReplyStatus = async (reply: CommunityReply, status: 'active' | 'hidden') => {
    setActioningId(reply.id);

    const { error } = await supabase.from('community_replies').update({ status }).eq('id', reply.id);

    if (error) {
      console.error(error);
      toast.error(`Could not ${status === 'hidden' ? 'hide' : 'restore'} that reply.`);
    } else {
      toast.success(status === 'hidden' ? 'Reply hidden from community.' : 'Reply restored.');
      await loadCommunity();
    }

    setActioningId(null);
  };

  const openReport = (target: { type: 'post' | 'reply'; id: string; preview: string }) => {
    setReportReason(REPORT_REASONS[0]);
    setReportDetails('');
    setReportTarget(target);
  };

  const submitReport = async () => {
    if (!user || !reportTarget) return;

    setReporting(true);

    const payload = {
      reporter_user_id: user.id,
      reason: reportReason,
      details: reportDetails.trim() || null,
      post_id: reportTarget.type === 'post' ? reportTarget.id : null,
      reply_id: reportTarget.type === 'reply' ? reportTarget.id : null,
    };

    const { error } = await supabase.from('community_reports').insert(payload);

    if (error) {
      console.error(error);
      toast.error('Could not send that report.');
    } else {
      toast.success('Report sent. Adulting will review it.');
      setReportTarget(null);
      setReportDetails('');
    }

    setReporting(false);
  };

  if (loading) {
    return <AppLoadingState title="Loading community" body="Pulling the live channels, posts, and moderation notices." />;
  }

  if (loadError) {
    return (
      <AppErrorState
        title="Community is unavailable"
        body={loadError}
        action={
          <AppButton tone="outline" onPress={() => void loadCommunity(true)}>
            Try again
          </AppButton>
        }
      />
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {communityWarnings.length > 0 ? (
        <AppInlineAlert title="Some community details are temporarily unavailable" tone="warning">
          {communityWarnings.join(' ')}
        </AppInlineAlert>
      ) : null}

      {communityReadOnly ? (
        <AppCard>
          <AppCardBody gap="$sm">
            <AppText fontSize="$sm" fontWeight="700">
              Community access {communityAccessStatus}
            </AppText>
            <AppText fontSize="$sm" color="$colorMuted">
              You can still browse channels and messages, but posting is paused on this account. Member notices, safety rules, and trusted routes now live under Health & wellbeing.
            </AppText>
            <AppButton alignSelf="flex-start" tone="outline" asChild>
              <Link to="/health">Open Health</Link>
            </AppButton>
          </AppCardBody>
        </AppCard>
      ) : null}

      <Tabs value={communityView} onValueChange={(value) => updateCommunityView(value as CommunityView)} className="w-full">
        <AppCard>
          <AppCardBody gap="$sm">
            <AppSectionHeader
              title="My Community"
              subtitle="Everything for talking to people in Adulting lives here."
            />
            <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-[22px] bg-muted/40 p-1.5">
              {communityViewOptions.map((option) => (
                <TabsTrigger
                  key={option.value}
                  value={option.value}
                  className="flex h-auto flex-col items-center gap-1 rounded-[18px] px-2 py-3 text-center data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground data-[state=active]:text-foreground">
                    {option.label}
                  </span>
                  <span className="text-sm font-bold text-foreground">{option.detail}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </AppCardBody>
        </AppCard>

        <TabsContent value="channels" className="space-y-5">
          {selectedCircle === 'all' ? (
            <>
              {ADULTING_COMMUNITY_CHANNEL_GROUPS.map((group) => (
                <div key={group.key} className="space-y-2">
                  <AppSectionHeader title={group.title} />
                  <div className="space-y-2">
                    {ADULTING_COMMUNITY_CHANNELS.filter((channel) => channel.group === group.key).map((channel) => {
                      const channelPosts = visiblePostsByChannel[channel.key];
                      const latestPost = channelPosts[0] || null;

                      return (
                        <AppCard key={channel.key}>
                          <AppCardBody flexDirection="row" alignItems="center" gap="$sm">
                            <AppPanelButton
                              onClick={() => openChannel(channel.key)}
                              className="bg-background px-0 py-0"
                            >
                              <div className="flex min-w-0 items-center gap-3 text-left">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xl">
                                  {channel.emoji}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <AppText fontSize="$sm" fontWeight="700" className="truncate">
                                    {channel.label}
                                  </AppText>
                                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" className="truncate">
                                  {channelPosts.length === 0
                                    ? 'No posts yet'
                                    : `${channelPosts.length} post${channelPosts.length === 1 ? '' : 's'} · Latest ${timeAgo(latestPost?.created_at || channelPosts[0].created_at)}`}
                                </AppText>
                              </div>
                                <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
                              </div>
                            </AppPanelButton>
                          </AppCardBody>
                        </AppCard>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <AppCard>
                <AppCardBody gap="$sm">
                  <AppButton alignSelf="flex-start" tone="outline" size="sm" onPress={() => setSelectedCircle('all')}>
                    Back to channels
                  </AppButton>
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xl">
                      {selectedCircleMeta.emoji}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <AppText fontSize="$sm" fontWeight="700">{selectedCircleMeta.label}</AppText>
                      </div>
                      <AppText fontSize="$xs" color="$colorMuted">
                        {filteredPosts.length} post{filteredPosts.length === 1 ? '' : 's'}
                      </AppText>
                    </div>
                  </div>
                </AppCardBody>
              </AppCard>

              {!communityReadOnly ? (
                <AppCard>
                  <AppCardHeader>
                    <AppText fontSize="$sm" fontWeight="700">Start a post</AppText>
                    <AppText fontSize="$xs" color="$colorMuted">{selectedCircleMeta.label}</AppText>
                  </AppCardHeader>
                  <AppCardBody>
                    <AppTextArea
                      id="community-post-body"
                      value={postBody}
                      onChange={(event) => setPostBody(event.target.value)}
                      placeholder={`What would help in ${selectedCircleMeta.label.toLowerCase()} right now?`}
                      maxLength={1200}
                      rows={4}
                      className="bg-background"
                    />
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[11px] text-muted-foreground">{postBody.trim().length}/1200</p>
                      <AppButton onPress={createPost} disabled={posting || !postBody.trim()}>
                        {posting ? <Loader2 className="animate-spin" /> : <Send />}
                        {posting ? 'Posting…' : 'Post to channel'}
                      </AppButton>
                    </div>
                  </AppCardBody>
                </AppCard>
              ) : null}

              <div className="space-y-4">
                {filteredPosts.length === 0 ? (
                  <AppEmptyState
                    title={`No posts in ${selectedCircleMeta.label} yet`}
                    body="Start the first post here so people have somewhere focused to begin. One honest question is enough."
                  />
                ) : (
                  filteredPosts.map((post) => {
                    const visibleReplies = (repliesByPost[post.id] || []).filter((reply) =>
                      reply.status === 'active' || reply.user_id === user?.id || isAdminOrMod
                    );
                    const topicMeta = getTopicMeta(post.topic as CommunityFeedFilter);

                    return (
                      <AppCard
                        key={post.id}
                        className={cn(
                          'space-y-4',
                          post.status === 'hidden' && 'border-dashed opacity-80'
                        )}
                      >
                        <AppCardBody>
                          <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary/30 text-sm font-semibold text-secondary-foreground">
                              {post.author_name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <AppText fontSize="$sm" fontWeight="700">{post.author_name}</AppText>
                                <AppText fontSize="$sm" color="$colorMuted">{timeAgo(post.created_at)}</AppText>
                                <Badge variant="outline" className="text-[10px]">
                                  {topicMeta.emoji} {topicMeta.label}
                                </Badge>
                                {post.user_id === user?.id ? <Badge variant="outline">You</Badge> : null}
                                {post.status === 'hidden' ? (
                                  <Badge variant="outline" className="border-amber-300 text-amber-700">Hidden</Badge>
                                ) : null}
                              </div>
                              <AppText fontSize="$sm" mt="$sm" className="whitespace-pre-wrap">
                                {post.body}
                              </AppText>
                            </div>
                          </div>

                          <AppButtonRow>
                            <CommunityActionButton onClick={() => setOpenReplyFor(openReplyFor === post.id ? null : post.id)}>
                              <MessageCircle size={14} />
                              Reply {visibleReplies.length > 0 ? `(${visibleReplies.length})` : ''}
                            </CommunityActionButton>

                            {post.user_id !== user?.id ? (
                              <CommunityActionButton onClick={() => openReport({ type: 'post', id: post.id, preview: post.body })}>
                                <Flag size={14} />
                                Report
                              </CommunityActionButton>
                            ) : null}

                            {post.user_id === user?.id ? (
                              <CommunityActionButton onClick={() => deletePost(post.id)} disabled={actioningId === post.id}>
                                <Trash2 size={14} />
                                Delete
                              </CommunityActionButton>
                            ) : null}

                            {isAdminOrMod ? (
                              <CommunityActionButton
                                onClick={() => updatePostStatus(post, post.status === 'hidden' ? 'active' : 'hidden')}
                                disabled={actioningId === post.id}
                              >
                                {post.status === 'hidden' ? <RotateCcw size={14} /> : <EyeOff size={14} />}
                                {post.status === 'hidden' ? 'Restore' : 'Hide'}
                              </CommunityActionButton>
                            ) : null}
                          </AppButtonRow>

                          {openReplyFor === post.id ? (
                            <AppCard backgroundColor="$background" p="$sm" className="animate-fade-in">
                              <AppFieldGroup label="Reply">
                                <AppTextArea
                                  value={replyDrafts[post.id] || ''}
                                  onChange={(event) =>
                                    setReplyDrafts((current) => ({ ...current, [post.id]: event.target.value }))
                                  }
                                  placeholder="Reply with something useful, kind, or practical."
                                  maxLength={600}
                                  rows={3}
                                />
                              </AppFieldGroup>
                              <div className="flex items-center justify-between gap-3">
                                <AppText fontSize="$sm" color="$colorMuted">{(replyDrafts[post.id] || '').trim().length}/600</AppText>
                                <AppButtonRow>
                                  <AppButton tone="outline" size="sm" onPress={() => setOpenReplyFor(null)}>
                                    Cancel
                                  </AppButton>
                                  <AppButton
                                    size="sm"
                                    onPress={() => saveReply(post.id)}
                                    disabled={savingReplyFor === post.id || !(replyDrafts[post.id] || '').trim()}
                                  >
                                    {savingReplyFor === post.id ? <Loader2 className="animate-spin" /> : <Send />}
                                    Reply
                                  </AppButton>
                                </AppButtonRow>
                              </div>
                            </AppCard>
                          ) : null}

                          {visibleReplies.length > 0 ? (
                            <div className="space-y-2">
                              {visibleReplies.map((reply) => {
                                const canManageReply = reply.user_id === user?.id || isAdminOrMod;

                                return (
                                  <AppCard
                                    key={reply.id}
                                    backgroundColor="$background"
                                    p="$sm"
                                    ml="$md"
                                    className={cn(reply.status === 'hidden' && 'border-dashed opacity-80')}
                                  >
                                    <div className="flex flex-wrap items-center gap-2">
                                      <AppText fontSize="$sm" fontWeight="700">{reply.author_name}</AppText>
                                      <AppText fontSize="$sm" color="$colorMuted">{timeAgo(reply.created_at)}</AppText>
                                      {reply.user_id === user?.id ? <Badge variant="outline">You</Badge> : null}
                                      {reply.status === 'hidden' ? (
                                        <Badge variant="outline" className="border-amber-300 text-amber-700">Hidden</Badge>
                                      ) : null}
                                    </div>
                                    <AppText fontSize="$sm" className="whitespace-pre-wrap">{reply.body}</AppText>
                                    <AppButtonRow>
                                      {reply.user_id !== user?.id ? (
                                        <CommunityActionButton onClick={() => openReport({ type: 'reply', id: reply.id, preview: reply.body })}>
                                          <Flag size={14} />
                                          Report
                                        </CommunityActionButton>
                                      ) : null}

                                      {reply.user_id === user?.id ? (
                                        <CommunityActionButton onClick={() => deleteReply(reply.id)} disabled={actioningId === reply.id}>
                                          <Trash2 size={14} />
                                          Delete
                                        </CommunityActionButton>
                                      ) : null}

                                      {isAdminOrMod && canManageReply ? (
                                        <CommunityActionButton
                                          onClick={() => updateReplyStatus(reply, reply.status === 'hidden' ? 'active' : 'hidden')}
                                          disabled={actioningId === reply.id}
                                        >
                                          {reply.status === 'hidden' ? <RotateCcw size={14} /> : <EyeOff size={14} />}
                                          {reply.status === 'hidden' ? 'Restore' : 'Hide'}
                                        </CommunityActionButton>
                                      ) : null}
                                    </AppButtonRow>
                                  </AppCard>
                                );
                              })}
                            </div>
                          ) : null}

                          {post.status === 'hidden' && post.user_id === user?.id ? (
                            <AppText fontSize="$sm" color="$colorMuted">
                              This post is only visible to you and moderators right now.
                            </AppText>
                          ) : null}
                        </AppCardBody>
                      </AppCard>
                    );
                  })
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="messages" className="space-y-5">
          {dmLoadError ? (
            <AppInlineAlert title="Direct messages are temporarily unavailable" tone="warning">
              {dmLoadError}
            </AppInlineAlert>
          ) : null}

          {selectedDmThreadId && selectedDmThreadSummary ? (
            <AppCard>
              <AppCardBody>
                <AppButton alignSelf="flex-start" tone="outline" size="sm" onPress={() => setSelectedDmThreadId(null)}>
                  Back to messages
                </AppButton>
                <AppText fontSize="$sm" fontWeight="700">
                  {selectedDmThreadSummary.title}
                </AppText>
                <div className="space-y-2">
                  {selectedDmThreadMessages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        'rounded-2xl px-3 py-3',
                        message.user_id === user?.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      )}
                    >
                      <AppText
                        fontSize="$xs"
                        fontWeight="700"
                        color={message.user_id === user?.id ? '$primaryForeground' : '$color'}
                      >
                        {message.author_name}
                      </AppText>
                      <AppText
                        fontSize="$sm"
                        color={message.user_id === user?.id ? '$primaryForeground' : '$color'}
                        className="whitespace-pre-wrap"
                      >
                        {message.body}
                      </AppText>
                      <AppText
                        fontSize="$xs"
                        color={message.user_id === user?.id ? '$primaryForeground' : '$colorMuted'}
                      >
                        {timeAgo(message.created_at)}
                      </AppText>
                    </div>
                  ))}
                </div>
                <AppFieldGroup label="Reply">
                  <AppTextArea
                    value={dmReplyBody}
                    onChange={(event) => setDmReplyBody(event.target.value)}
                    placeholder="Send the next message in this thread."
                    maxLength={1200}
                    rows={3}
                  />
                </AppFieldGroup>
                <div className="flex justify-end">
                  <AppButton onPress={sendDirectMessage} disabled={dmSaving || !dmReplyBody.trim()}>
                    {dmSaving ? <Loader2 className="animate-spin" /> : <Send />}
                    {dmSaving ? 'Sending…' : 'Send message'}
                  </AppButton>
                </div>
              </AppCardBody>
            </AppCard>
          ) : (
            <div className="space-y-4">
              <AppCard>
                <AppCardBody>
                  {dmLoading ? (
                    <AppLoadingState title="Loading messages" body="Pulling your direct-message threads." />
                  ) : dmThreadSummaries.length ? (
                    <div className="space-y-2">
                      {dmThreadSummaries.map((thread) => (
                        <AppPanelButton
                          key={thread.id}
                          onClick={() => setSelectedDmThreadId(thread.id)}
                          className="bg-background px-4 py-3 text-left"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                              {(thread.title || 'D').charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <AppText fontSize="$sm" fontWeight="700" className="truncate">
                                  {thread.title}
                                </AppText>
                                <AppText fontSize="$xs" color="$colorMuted">
                                  {thread.lastMessageAt ? timeAgo(thread.lastMessageAt) : 'New'}
                                </AppText>
                              </div>
                              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" className="truncate">
                                {thread.latestMessage?.body || 'No messages yet'}
                              </AppText>
                            </div>
                            <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
                          </div>
                        </AppPanelButton>
                      ))}
                    </div>
                  ) : (
                    <AppEmptyState
                      title="No direct messages yet"
                      body="Start the first one below when a channel conversation needs a one-to-one follow-up."
                    />
                  )}
                </AppCardBody>
              </AppCard>

              <AppCard>
                <AppCardHeader>
                  <AppText fontSize="$sm" fontWeight="700">Start a message</AppText>
                </AppCardHeader>
                <AppCardBody>
                  <AppFieldGroup label="Member">
                    <Select value={selectedDmRecipientId} onValueChange={setSelectedDmRecipientId}>
                      <SelectTrigger>
                        <SelectValue placeholder={dmMemberOptions.length ? 'Choose a member' : 'No members available yet'} />
                      </SelectTrigger>
                      <SelectContent>
                        {dmMemberOptions.map((option) => (
                          <SelectItem key={option.userId} value={option.userId}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </AppFieldGroup>
                  <AppFieldGroup label="First message">
                    <AppTextArea
                      value={newDmBody}
                      onChange={(event) => setNewDmBody(event.target.value)}
                      placeholder="Start with one useful question or one clear follow-up."
                      maxLength={1200}
                      rows={4}
                    />
                  </AppFieldGroup>
                  <AppButton onPress={startDirectMessage} disabled={dmSaving || !selectedDmRecipientId || !newDmBody.trim()}>
                    {dmSaving ? <Loader2 className="animate-spin" /> : <Send />}
                    {dmSaving ? 'Sending…' : 'Start direct message'}
                  </AppButton>
                </AppCardBody>
              </AppCard>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!reportTarget} onOpenChange={(open) => !open && setReportTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Report content</DialogTitle>
            <DialogDescription className="leading-relaxed">
              Reports go to Adulting moderation. Use them for spam, harm, or content that makes the space feel unsafe.
            </DialogDescription>
          </DialogHeader>

          {reportTarget ? (
            <div className="space-y-4">
              <AppCard backgroundColor="$mutedBackground" p="$sm">
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  Preview
                </AppText>
                <AppText fontSize="$sm" className="whitespace-pre-wrap">
                  {reportTarget.preview.slice(0, 180)}
                  {reportTarget.preview.length > 180 ? '…' : ''}
                </AppText>
              </AppCard>

              <AppCard backgroundColor="$background" p="$sm">
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  What happens next
                </AppText>
                <AppText fontSize="$sm">
                  Adulting moderation will mark this as open, move it into review if needed, then close it as actioned or dismissed.
                </AppText>
              </AppCard>

              <AppFieldGroup label="Reason">
                <div className="flex flex-wrap gap-2">
                  {REPORT_REASONS.map((reason) => (
                    <AppChipButton key={reason} active={reportReason === reason} onClick={() => setReportReason(reason)}>
                      {reason}
                    </AppChipButton>
                  ))}
                </div>
              </AppFieldGroup>

              <AppFieldGroup label="Anything else to add?">
                <AppTextArea
                  value={reportDetails}
                  onChange={(event) => setReportDetails(event.target.value)}
                  rows={4}
                  maxLength={600}
                  placeholder="Optional details for moderation."
                />
              </AppFieldGroup>

              <AppButtonRow>
                <AppButton flex={1} tone="outline" onPress={() => setReportTarget(null)} disabled={reporting}>
                  Cancel
                </AppButton>
                <AppButton flex={1} onPress={submitReport} disabled={reporting}>
                  {reporting ? <Loader2 className="animate-spin" /> : <Flag />}
                  {reporting ? 'Sending…' : 'Send report'}
                </AppButton>
              </AppButtonRow>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommunityActionButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <AppButton size="sm" tone="outline" onPress={onClick} disabled={disabled}>
      {children}
    </AppButton>
  );
}
